import { z } from 'zod'
import OrgEntityDtoSchema from './OrgEntityDtoSchema'

export namespace OrgDto {
   export const entitySchema = OrgEntityDtoSchema
   export type entity = z.output<typeof OrgEntityDtoSchema>

   export type entityWithNames = OrgDto.entity & {
      orgTypeName: string
      adminUserName: string
      lastUpdatedByUserName: string
      taxOrgName?: string // not always set though! We should make this stricter so we can always expect / not expect it
      logoUrl?: string // not always set though!
   }

   export const minimalSchema = OrgDto.entitySchema
      .pick({
         id: true,
         name: true,
         website: true,
         contactNumber: true,
         logo: true,
         address: true,
         orgType: true,
         orgSize: true,
         xeroAuthorised: true,
         countryCode: true,
         currencyCode: true,
         taxDescription: true,
         xeroDisconnectedViaXPM: true,
         xpmName: true,
         businessPlanFinalisedDate: true,
         tierMaxUsers: true,
         trialEndDate: true,
         taxPercentage: true,
         adminUserId: true,
         chargebee: true,
      })
      .merge(
         z.object({
            currencySymbol: z.string().optional(),
            adminUserName: z.string(),
            logoUrl: z.string().optional(),
         })
      )

   export type minimal = z.output<typeof minimalSchema>

   export const listSchema = OrgDto.entitySchema
      .pick({
         id: true,
         name: true,
         tierName: true,
         advisorOrgId: true,
         orgType: true,
         xeroAuthorised: true,
         xeroLastUpdated: true,
         createdAt: true,
      })
      // Orgs created before the 2024-06 rename from `created` still store the old name and were
      // never backfilled, so createdAt can be missing.
      .partial({ createdAt: true })
      .merge(
         z.object({
            adminUserName: z.string(),
            advisorName: z.string().optional(),
         })
      )

   export type list = z.output<typeof listSchema>

   export const editAdvisorSchema = OrgDto.entitySchema.pick({
      id: true,
      name: true,
      taxOrgId: true,
      taxDescription: true,
      taxPercentage: true,
      website: true,
      contactNumber: true,
      address: true,
      trialEndDate: true,
      tierName: true,
      tierMaxUsers: true,
      countryCode: true,
      currencyCode: true,
      timezone: true,
      demoDocId: true,
      accountStatus: true,
   })
   export type editAdvisor = z.output<typeof editAdvisorSchema>

   export const editAdvisorAsAdvisorSchema = OrgDto.editAdvisorSchema.omit({
      trialEndDate: true,
      tierName: true,
      tierMaxUsers: true,
      demoDocId: true,
      accountStatus: true,
   })
   export type editAdvisorAsAdvisor = z.output<typeof editAdvisorAsAdvisorSchema>

   export const editClientSchema = OrgDto.entitySchema.pick({
      id: true,
      name: true,
      contactNumber: true,
   })

   export type editClient = z.output<typeof editClientSchema>

   export const createSchema = OrgDto.entitySchema
      .pick({
         name: true,
         tierName: true,
         orgType: true,
         advisorOrgId: true,
         taxDescription: true,
         taxPercentage: true,
      })
      .merge(
         z.object({
            id: OrgDto.entitySchema.shape.id.optional(),
            adminUserId: OrgDto.entitySchema.shape.adminUserId.optional(),
         })
      )

   export type create = z.output<typeof createSchema>

   export const createGapSchema = OrgDto.entitySchema.pick({
      id: true,
      name: true,
      orgType: true,
   })
   export type createGap = z.output<typeof OrgDto.createGapSchema>

   export const createTrialSchema = OrgDto.entitySchema
      .pick({
         name: true,
         website: true,
         countryCode: true,
         currencyCode: true,
         taxDescription: true,
         taxPercentage: true,
         timezone: true,
      })
   export type createTrial = z.output<typeof OrgDto.createTrialSchema>
}
